import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import API from "../../../Network/API";
import { DOOR_CONFIG_SECTIONS } from "./doorConfigSchema";
import ConfigFormRow from "./props/ConfigFormRow";

export default function DoorConfiguration({ siteId }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeSection, setActiveSection] = useState(DOOR_CONFIG_SECTIONS[0].id);
    const [site, setSite] = useState(null);
    const [form, setForm] = useState({});

    const loadConfiguration = (query) => {
        setLoading(true);
        API.get(`/v1/oauth/door-configuration/${encodeURIComponent(query)}`)
            .then((response) => {
                setSite(response.data.data.site);
                setForm(response.data.data.form || {});
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message || "Failed to load door configuration");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (siteId) {
            loadConfiguration(siteId);
        }
    }, [siteId]);

    const handleChange = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = (event) => {
        event.preventDefault();
        if (!site) return;

        setSaving(true);
        const payload = new FormData();
        payload.append("site_id", site.site_id);
        payload.append("customer_site_id", site.customer_site_id);
        payload.append("device_id", site.device_id || "");
        payload.append("door_id", site.door_id || site.site_name || "");

        const switchNames = new Set();
        DOOR_CONFIG_SECTIONS.forEach((section) => {
            section.fields.forEach((field) => {
                if (field.type === "switch") {
                    switchNames.add(field.name);
                    if (form[field.name]) {
                        payload.append(field.name, "1");
                    }
                }
            });
        });

        Object.entries(form).forEach(([key, value]) => {
            if (switchNames.has(key)) {
                return;
            }
            payload.append(key, value ?? "");
        });

        API.post("/v1/oauth/door-configuration/store", payload)
            .then((response) => {
                toast.success(response.data.message || "Configuration saved");
                loadConfiguration(site.site_id);
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message || "Failed to save configuration");
            })
            .finally(() => setSaving(false));
    };

    const currentSection = DOOR_CONFIG_SECTIONS.find((section) => section.id === activeSection)
        || DOOR_CONFIG_SECTIONS[0];

    if (loading) {
        return (
            <div className="door-config-panel p-5 text-center">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                <p className="mt-2 mb-0 text-muted">Loading door configuration...</p>
            </div>
        );
    }

    return (
        <div className="door-config-panel">
            <div className="door-config-panel__head">
                <h6 className="mb-1">ACS Door Configuration</h6>
                <p className="mb-0 text-muted small">Configure door, vibration, alerts and protection settings for this site.</p>
                {site ? (
                    <div className="door-config-panel__site">
                        <span className="door-config-panel__site-pill"><strong>Site:</strong> {site.site_id}</span>
                        <span className="door-config-panel__site-pill"><strong>Operator:</strong> {site.customer_site_id}</span>
                        <span className="door-config-panel__site-pill"><strong>Name:</strong> {site.site_name}</span>
                    </div>
                ) : null}
            </div>

            <div className="door-config-section-tabs">
                {DOOR_CONFIG_SECTIONS.map((section) => (
                    <button
                        key={section.id}
                        type="button"
                        className={`door-config-section-tab ${activeSection === section.id ? "active" : ""}`}
                        onClick={() => setActiveSection(section.id)}
                    >
                        <i className={`${section.icon} me-1`} />
                        {section.title}
                    </button>
                ))}
            </div>

            <form onSubmit={handleSave}>
                <div className="door-config-form-body">
                    <h6 className="mb-3">{currentSection.title}</h6>
                    {currentSection.fields.map((field) => (
                        <ConfigFormRow
                            key={`${currentSection.id}-${field.name || field.prefix}`}
                            field={field}
                            form={form}
                            onChange={handleChange}
                        />
                    ))}
                </div>

                <div className="door-config-actions">
                    <button type="button" className="btn btn-light" onClick={() => loadConfiguration(siteId)}>
                        Reset
                    </button>
                    <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                        {saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fa-light fa-save me-1" />
                                Save Configuration
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
